import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import AreaChartIcon from '@mui/icons-material/AreaChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShieldIcon from '@mui/icons-material/Shield';
import CalculateIcon from '@mui/icons-material/Calculate';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import LastUpdate from '../last-update/LastUpdate';
import { DrawerItemText } from './DrawerItemText';
import { clearAppSelections } from '../../services/qlikSelectionService';
import { ExtraControlsConfig } from '../../types';
import { ShareModal } from '../share-modal/ShareModal';
import PdfReportModal from '../pdf-report-modal/PdfReportModal';
import { QLIK_APP_REF } from '../../constants';

interface NavDrawerContentProps {
  expanded: boolean;
  extraControls?: ExtraControlsConfig;
  isMdDown: boolean;
  onCustomizeReport?: () => void;
}

export const NavDrawerContent: React.FC<NavDrawerContentProps> = ({
  expanded,
  extraControls,
  isMdDown,
  onCustomizeReport,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [pdfReportModalOpen, setPdfReportModalOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const onStartOver = async () => {
    await clearAppSelections(QLIK_APP_REF);
  };

  const handleShareClick = () => {
    setShareModalOpen(true);
  };

  const handleShareClose = () => {
    setShareModalOpen(false);
  };

  const handleCustomizeReportClick = () => {
    setPdfReportModalOpen(true);
    if (onCustomizeReport) {
      onCustomizeReport();
    }
  };

  const handlePdfReportClose = () => {
    setPdfReportModalOpen(false);
  };

  // Main navigation items
  const mainItems = (
    <List dense sx={{ py: 0 }}>
      <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/incident-growth')}
        onClick={() => navigate('/incident-growth')}
      >
        <ListItemIcon>
          <AreaChartIcon />
        </ListItemIcon>
        <DrawerItemText primary="Incident Activity Growth" expanded={expanded} />
      </ListItemButton>

      <Divider />

      <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/costs-breakdown')}
        onClick={() => navigate('/costs-breakdown')}
      >
        <ListItemIcon>
          <AttachMoneyIcon />
        </ListItemIcon>
        <DrawerItemText primary="Chubb Costs Breakdown" expanded={expanded} />
      </ListItemButton>

      <Divider />

      {/* <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/assessments')}
        onClick={() => navigate('/assessments')}
      >
        <ListItemIcon>
          <ShieldIcon />
        </ListItemIcon>
        <DrawerItemText primary="Assessments" expanded={expanded} />
      </ListItemButton> */}

      <Divider />

      <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/cyber-risk-calculators')}
        onClick={() => navigate('/cyber-risk-calculators')}
      >
        <ListItemIcon>
          <CalculateIcon />
        </ListItemIcon>
        <DrawerItemText primary="Cyber Risk Calculators" expanded={expanded} />
      </ListItemButton>

      <Divider />

      <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/peer-purchasing-insights')}
        onClick={() => navigate('/peer-purchasing-insights')}
      >
        <ListItemIcon>
          <PeopleIcon />
        </ListItemIcon>
        <DrawerItemText primary="Peer Purchasing Insights" expanded={expanded} />
      </ListItemButton>

      <Divider />

      <ListItemButton
        sx={{ minHeight: 56 }}
        selected={isActive('/cyber-library')}
        onClick={() => navigate('/cyber-library')}
      >
        <ListItemIcon>
          <MenuBookIcon />
        </ListItemIcon>
        <DrawerItemText primary="Cyber Library" expanded={expanded} />
      </ListItemButton>

      <Divider />
    </List>
  );

  // Bottom drawer items
  const bottomItems = (
    <List dense sx={{ py: 0 }}>
      {/* Show PDF button on mobile/small screens */}
      {isMdDown && (
        <ListItemButton sx={{ minHeight: 56 }} onClick={handleCustomizeReportClick}>
          <ListItemIcon>
            <PictureAsPdfIcon />
          </ListItemIcon>
          <DrawerItemText primary="Customize Report" expanded={expanded} />
        </ListItemButton>
      )}

      {/* Show Start Over on desktop */}
      {!isMdDown && extraControls?.startOver?.enable && (
        <ListItemButton sx={{ minHeight: 56 }} onClick={onStartOver}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <DrawerItemText primary="Start Over" expanded={expanded} />
        </ListItemButton>
      )}

      <Divider />

      <ListItemButton sx={{ minHeight: 56 }} onClick={handleShareClick}>
        <ListItemIcon>
          <ShareIcon />
        </ListItemIcon>
        <DrawerItemText primary="Share with Colleagues" expanded={expanded} />
      </ListItemButton>

      <Divider />

      {/* Show Last Update on desktop */}
      {!isMdDown && extraControls?.lastUpdate?.enable && (
        <ListItemButton sx={{ minHeight: 56 }}>
          <ListItemIcon>
            <StorageIcon />
          </ListItemIcon>
          <LastUpdate componentLocation={extraControls?.lastUpdate?.componentLocation} expanded={expanded} />
        </ListItemButton>
      )}
    </List>
  );

  return (
    <>
      <Box
        role="presentation"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Main items at top */}
        <Box sx={{ flex: '0 0 auto' }}>{mainItems}</Box>

        {/* Spacer to push bottom items down */}
        <Box sx={{ flex: '1 1 auto' }} />

        {/* Bottom items */}
        {bottomItems && (
          <>
            <Divider />
            <Box sx={{ flex: '0 0 auto', backgroundColor: 'rgba(255, 255, 255, 0.25)' }}>
              {bottomItems}
            </Box>
          </>
        )}
      </Box>
      {/* Share Modal */}
      <ShareModal open={shareModalOpen} onClose={handleShareClose} />
      {/* PDF Report Modal */}
      <PdfReportModal open={pdfReportModalOpen} onClose={handlePdfReportClose} />
    </>
  );
};